import React, { useEffect, useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import useTrans from '@hooks/useTrans'
import { cn } from '@utils/safeTs'
import { EditorErrorType, EditorStatusType } from '@utils/types'
import { Close, FileAi, FileEps, Plus, Pdf } from '@components/icons'
import { Alert, Button, FormField, TutorialItem } from '@components/atoms'
import EditorSidebarImage from '@components/molecules/EditorSidebarImage/EditorSidebarImage'
import styles from './EditorImagesSidebar.module.sass'
import { useEditorContext } from 'context/EditorContext'
import { EDITOR_FILE_EXTENSIONS } from '@utils/editor-bozze'
import { useTutorialContext } from 'context/TutorialContext'

const MIME_MAP: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.svg': 'image/svg+xml',
  '.ai': 'application/postscript',
  '.pdf': 'application/pdf',
  '.eps': 'application/postscript',
}

const SUPPORTED_PREVIEW_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml']

const isPreviewable = (file: File) => {
  return file && SUPPORTED_PREVIEW_TYPES.includes(file.type)
}

interface SelectedFile {
  file: File
  filename?: string
  preview?: string
  icon?: React.ReactNode
}

interface Props {
  status?: EditorStatusType
  onAdd?: (value: any) => void | Promise<void>
  errors?: EditorErrorType[]
  className?: string
}

const EditorImagesSidebar = (props: Props) => {
  const { status, onAdd, errors = [], className = '' } = props

  const t = useTrans()
  const { activeCanvas, colorLimits } = useEditorContext()
  const { nextTutorialItem } = useTutorialContext()

  const dropzoneAccept = useMemo(() => {
    const estensioni = EDITOR_FILE_EXTENSIONS // status?.tecnica?.estensioni_ammesse || []
    const accept: Record<string, string[]> = {}

    estensioni.forEach((ext) => {
      const dotExt = ext.startsWith('.') ? ext : `.${ext}`
      const mime = MIME_MAP[dotExt.toLowerCase()] || 'application/octet-stream'

      if (!accept[mime]) {
        accept[mime] = []
      }
      accept[mime].push(dotExt)
    })

    return accept
  }, [])

  const formatiSupportati = useMemo(() => Object.values(dropzoneAccept).flat(), [dropzoneAccept])

  const [file, setFile] = useState<SelectedFile | undefined>(undefined)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleDropzoneInteract = () => {
    if (!isSubmitting) nextTutorialItem('editor', 2)
  }

  const buildSelectedFile = (selectedFile: File): SelectedFile => ({
    file: selectedFile,
    filename: selectedFile.name,
    preview: isPreviewable(selectedFile) ? URL.createObjectURL(selectedFile) : undefined,
    icon: isPreviewable(selectedFile) ? undefined : 'application/pdf' == selectedFile.type ? (
      <Pdf />
    ) : selectedFile.type === 'application/postscript' && selectedFile.name?.includes('.ai') ? (
      <FileAi />
    ) : selectedFile.type === 'application/postscript' && selectedFile.name?.includes('.eps') ? (
      <FileEps />
    ) : undefined,
  })

  const clearFile = () => {
    setFile(undefined)
    setErrorMessage(undefined)
  }

  const handleFileSelection = async (selectedFile: File) => {
    if (!selectedFile || !onAdd || isSubmitting) return

    nextTutorialItem('editor', 2)
    setFile(buildSelectedFile(selectedFile))
    setIsSubmitting(true)
    setErrorMessage(undefined)

    try {
      await Promise.resolve(onAdd(selectedFile))
      setFile(undefined)
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('Si è verificato un errore'))
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    return () => {
      if (file?.preview) URL.revokeObjectURL(file.preview)
    }
  }, [file?.preview])

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: dropzoneAccept,
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
    disabled: isSubmitting,
    onDropAccepted: (acceptedFiles) => {
      const selectedFile = acceptedFiles?.[0]
      if (selectedFile) handleFileSelection(selectedFile)
    },
  })

  const imagesInCanvas = useMemo(() => {
    return activeCanvas?.data?.filter((oc) => oc.etype === 'image') || []
  }, [activeCanvas?.data])

  return (
    <div className={cn(styles.root, className)}>
      {imagesInCanvas?.length > 0 && (
        <div className={styles.body}>
          <div className={styles.list}>
            {imagesInCanvas?.map((i) => (
              <EditorSidebarImage
                key={i.id}
                image={i}
                selected={i.isSelected}
                onRemove={() => activeCanvas?.actions?.handleObjectAction(i.id, 'delete')}
                onColorChange={(id, o, n) => {
                  activeCanvas?.actions?.handleColorChange(i.id, o, n)
                }}
                onColorVisibilityChange={(id, colorId, visible) =>
                  activeCanvas?.actions?.handleColorVisibilityChange(id, colorId, visible)
                }
                onMonochromaticSwitch={(id, v) =>
                  activeCanvas?.actions?.handleMonochromaticChange(id, v)
                }
                onNoBackgroundSwitch={(id, v) =>
                  activeCanvas?.actions?.handleNoBackgroundChange(id, v)
                }
                error={errors?.find((e) => e.objectId == i.id)}
                colorLimits={colorLimits}
              />
            ))}
          </div>
        </div>
      )}
      <div className={styles.new}>
        <TutorialItem
          tutorialKey={'editor'}
          index={1}
          direction="right"
          content={t(
            'Trascina il file oppure premi su "clicca qui" per inserirlo nell’area di stampa del prodotto'
          )}
          overlayClassName={styles.tutorialItemOverlay}
        >
          <FormField
            // label={t('Nuova immagine')}
            styleVariant="editor"
            id="editorimagesidebar_new_file"
            // status={errorMessage ? 'error' : 'no-status'}
            // errorMessage={errorMessage}
            // infoMessage={
            //   formatiSupportati.length > 0
            //     ? t('Formati supportati: {0}', [formatiSupportati.join(', ')])
            //     : undefined
            // }
          >
            <div
              {...getRootProps({
                className: cn(styles.dropzone, isSubmitting ? styles['dropzone--disabled'] : ''),
                onClick: () => {
                  handleDropzoneInteract()
                  open()
                },
                onDragEnter: handleDropzoneInteract,
              })}
            >
              <input {...getInputProps()} />
              <p className={styles.dropzoneLabel}>{t('Trascina il file o clicca qui')}</p>
              <Button
                className={styles.newCtaButton}
                variant="primary"
                label={t('Aggiungi un logo')}
                icon={<Plus />}
                icon_pos="right"
                disabled={isSubmitting}
                loading={isSubmitting}
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  nextTutorialItem('editor', 2)
                  open()
                }}
              />
            </div>
          </FormField>
        </TutorialItem>
        {!!errorMessage && (
          <Alert variant="danger" placeholder={errorMessage} styleVariant="background" />
        )}
        {!!file && (
          <div className={styles.preview}>
            {file.preview ? (
              <img src={file.preview} className={styles.previewImage} />
            ) : file.icon ? (
              <span className={styles.previewImage}>{file.icon}</span>
            ) : null}
            <div className={styles.previewContent}>
              <span className={styles.previewLabel}>{file.filename}</span>
              {isSubmitting && (
                <span className={styles.previewStatus}>{t('Caricamento in corso')}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default EditorImagesSidebar
